import { useMutation, useQueryClient } from "@tanstack/react-query";
import { leadListKeys } from "Api/Queries/Lead/useGetLeadListQuery";
import {
  type ApiProblemDetails,
  type FetchResponse,
  type InviteLeadRequest,
  type LeadInvitationCommandResult,
  LeadInvitationCommandResultStatus,
  postLeadsInvite,
  LeadStatus,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { invalidateQueriesByFilter } from "Utils/queryUtils";

type Props = {
  onSettled?: (data: LeadInvitationCommandResultStatus) => void;
};

export const useInviteLeadMutation = ({ onSettled }: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      (data: InviteLeadRequest) => postLeadsInvite(data),
    ),
    onSuccess: () => {
      invalidateQueriesByFilter(queryClient, leadListKeys.all, {
        leadStatus: LeadStatus.Invited,
      });
    },
    onError: () => {}, //Specifying, so the global error handling is not triggered, but not doing anything otherwise
    onSettled: (
      data: LeadInvitationCommandResult | undefined,
      error: FetchResponse<ApiProblemDetails, 409> | null,
    ) => {
      if (data) {
        onSettled?.(data.status);
        return;
      }

      if (!error) {
        return;
      }

      if (error.data.status !== 409) {
        onSettled?.(LeadInvitationCommandResultStatus.Error);
        return;
      }

      if (error.data.errors.User) {
        onSettled?.(LeadInvitationCommandResultStatus.UserExists);
        return;
      }

      if (error.data.errors.Lead) {
        onSettled?.(LeadInvitationCommandResultStatus.LeadExists);
        return;
      }
    },
  });
};
